/* eslint-disable max-len */
import React from 'react';
import {useTranslation} from 'react-i18next';

import InlineNav from '../InlineNav';
import styled, {css} from 'styled-components';
import {useHistory} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {
    Flex,
    mobileMediaQuery,
    tabletMediaQuery,
    PageBodyWrapper,
    useIsMobile,
    notWebMediaQuery,
    useBreakpoint,
} from './../../styles';

import WarnerLogo from './../../assets/warner-logo.png';

import Apple from './../../assets/apple.svg';
import GooglePlay from './../../assets/google_play.svg';
import {getCurrentLang} from '../../utils/i18n';
import {findOTConfig} from '../../utils/onetrust';
import FooterCNitoButton from './FooterCNitoButton';
import FooterCNButton from './FooterCNButton';
import FooterDKidsButton from './FooterDKidsButton';
import {selectDKidsEnabled} from '../../store/reducers';
import {debugCNitoUrl, getBrand, useBrand} from '../../helpers';

export const links = {
    privacy: {
        'en-US':
            'https://www.warnermediaprivacy.com/policycenter/b2c/children/en-us/',
        'pt-BR':
            'https://www.warnermediaprivacy.com/policycenter/b2c/children/pt-br/',
        'es-ES':
            'https://www.warnermediaprivacy.com/policycenter/b2c/children/es/',
    },
    terms: {
        'en-US':
            'https://cnapp.zendesk.com/hc/es-419/articles/360040782691-Términos-y-Condiciones-De-Uso-CN-App',
        'pt-BR':
            'https://cnapp.zendesk.com/hc/pt-br/articles/360040782691-Termos-e-Condições-de-Uso-do-CN-APP',
        'es-ES':
            'https://cnapp.zendesk.com/hc/es-419/articles/360040782691-Términos-y-Condiciones-De-Uso-CN-App',
    },
    help: {
        'en-US':
            'https://cnapp.zendesk.com/hc/en-us/categories/360002608911-Frequently-Asked-Questions',
        'pt-BR':
            'https://cnapp.zendesk.com/hc/pt-br/categories/360002608911-Perguntas-Frequentes-',
        'es-ES':
            'https://cnapp.zendesk.com/hc/es-419/categories/360002608911-Preguntas-Frecuentes',
    },
    programmingDKids: {
        'pt-BR': 'https://www.discoverybrasil.com/kids/tv/programacao',
        'es-ES': 'https://www.tudiscovery.com/kids/tv/programacion',
    },
    privacyDKids: {
        'pt-BR': `/politica-de-privacidade${debugCNitoUrl()}`,
        'es-ES': `/politica-de-privacidad${debugCNitoUrl()}`,
    },
    termsDKids: {
        'pt-BR': `/termos-de-uso${debugCNitoUrl()}`,
        'es-ES': `/terminos-de-uso${debugCNitoUrl()}`,
    },
};

const getCNitoProgrammingLink = lang => {
    if (
        (process.env.REACT_APP_STAGE || 'dev') === 'prod' &&
        getBrand() !== 'cnito'
    ) {
        //use real domain based on lang
        if (lang === 'es') {
            return 'https://www.cartoonitola.com/programacion';
        } else {
            return 'https://www.cartoonito.com.br/programacion';
        }
    } else {
        //use dev domain
        return lang === 'es'
            ? `/programacion?cnito=true`
            : `/programacao?cnito=true`;
    }
};

const getCNAppProgrammingLink = lang => {
    if (
        (process.env.REACT_APP_STAGE || 'dev') === 'prod' &&
        getBrand() !== 'cnapp'
    ) {
        //use real domain based on lang if not on cnapp
        if (lang === 'es') {
            return 'https://cnapp.cartoonnetworkla.com/programacion';
        } else {
            return 'https://cnapp.cartoonnetwork.com.br/programacion';
        }
    } else {
        //use dev domain
        return lang === 'es' ? `/programacion` : `/programacao`;
    }
};

const Footer = ({paddingBottom}) => {
    const {t} = useTranslation();
    const history = useHistory();
    const config = useSelector(state => state.config);
    const user = useSelector(state => state.user);
    const isMobile = useIsMobile();
    const breakpoint = useBreakpoint();
    const dkidsEnabled = useSelector(selectDKidsEnabled);

    const brand = useBrand();

    const noBottomNav = () => {
        let show = false;

        if (
            ['/'].includes(history.location.pathname) ||
            history.location.pathname.indexOf('/play') >= 0 ||
            history.location.pathname.indexOf('/shows/') >= 0 ||
            history.location.pathname.indexOf('/now/') >= 0
        ) {
            show = true;
        }

        return show;
    };

    const showDetailsBTW = () => {
        let isShowDetailsBTW = false;

        if (
            history.location.pathname.indexOf('/shows/') >= 0 &&
            history.location.pathname.indexOf('/season/') > 0 &&
            config.btw &&
            config.btw.enabled === true &&
            (!user || !user.jwtToken)
        ) {
            isShowDetailsBTW = true;
        }

        return isShowDetailsBTW;
    };

    const infoLinks = [
        {
            title: t('footerV2.privacyPolicy'),
            url:
                links.privacy[window.localStorage.i18nextLng] ||
                links.privacy['pt-BR'],
        },
        {
            title: t('footerV2.FAQandHelp'),
            url:
                links.help[window.localStorage.i18nextLng] ||
                links.help['pt-BR'],
        },
        {
            title: t('footerV2.schedule'),
            url: getCNAppProgrammingLink(getCurrentLang()),
        },
        {
            title: t('footerV2.scheduleCNito'),
            url: getCNitoProgrammingLink(getCurrentLang()),
        },
    ];

    const scheduleLinks = [
        {
            title: 'Programação Efetivamente veiculada Cartoon Network',
            url:
                'https://www.turnertapkit.com/ancine/networkFilesExternal?networkId=cnhd',
        },
        {
            title: 'Programação Efetivamente Veiculada Cartoonito',
            url: 'https://www.turnertapkit.com/ancine/',
        },
    ];

    const dkidsLinks = [
        {
            title: t('footerV2.privacyPolicy'),
            url:
                links.privacyDKids[window.localStorage.i18nextLng] ||
                links.privacyDKids['pt-BR'],
        },
        {
            title: t('footerV2.termsOfUse'),
            url:
                links.termsDKids[window.localStorage.i18nextLng] ||
                links.termsDKids['pt-BR'],
        },
        {
            title: t('dkids.footerProgramming'),
            url:
                links.programmingDKids[window.localStorage.i18nextLng] ||
                links.programmingDKids['pt-BR'],
        },
    ];

    if (
        brand === 'cnito' &&
        (history.location.pathname.indexOf('/play') >= 0 ||
            history.location.pathname.indexOf('/live/') >= 0)
    ) {
        return null;
    }

    const hasOT = !!findOTConfig();

    return (
        <Container
            noBottomNav={noBottomNav()}
            showDetailsBTW={showDetailsBTW()}
            paddingBottom={
                brand === 'dkids' ? paddingBottom + 40 : paddingBottom
            }
            brand={brand}
        >
            <Wrapper>
                <Flex
                    direction={
                        breakpoint === 'xl' && brand === 'dkids'
                            ? 'row'
                            : 'column'
                    }
                    flex="1"
                    width="100%"
                    maxWidth="100%"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                >
                    <LogoRow brand={brand}>
                        <img
                            src={WarnerLogo}
                            width="161px"
                            height="37px"
                            alt="Turner Logo"
                        />
                        {(brand === 'cnito' ||
                            (brand === 'dkids' && dkidsEnabled)) && (
                            <FooterCNButton />
                        )}
                        {brand !== 'dkids' && dkidsEnabled && (
                            <FooterDKidsButton />
                        )}
                        {(brand === 'cnapp' ||
                            (brand === 'dkids' && dkidsEnabled)) && (
                            <FooterCNitoButton />
                        )}
                    </LogoRow>

                    {brand !== 'dkids' && (
                        <FooterDescription>
                            {t('footerV2.description')}
                        </FooterDescription>
                    )}

                    {brand !== 'dkids' && (
                        <Flex
                            justifyContent="flex-start"
                            margin="0 0 20px 0"
                            wrap={isMobile ? 'wrap' : 'no-wrap'}
                        >
                            <ImageLink
                                href="https://play.google.com/store/apps/details?id=com.turner.cnja.br"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img
                                    src={GooglePlay}
                                    width="135px"
                                    height="41px"
                                    alt="Get it on GooglePlay"
                                />
                            </ImageLink>
                            <ImageLink
                                href="https://apps.apple.com/us/app/cartoon-network-já/id1290928633"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img
                                    src={Apple}
                                    width="135px"
                                    height="41px"
                                    alt="Get it on AppStore"
                                />
                            </ImageLink>

                            <DownloadText>
                                {t('footerV2.downloadApp')}
                            </DownloadText>
                        </Flex>
                    )}

                    <DisclaimerContainer
                        horizontal={brand === 'dkids' && breakpoint === 'xl'}
                    >
                        <Flex
                            wrap="wrap"
                            alignItems="flex-start"
                            justifyContent="flex-start"
                        >
                            <InlineNav
                                items={
                                    brand === 'dkids' ? dkidsLinks : infoLinks
                                }
                                gap="32"
                            />

                            {window.localStorage.i18nextLng === 'pt-BR' &&
                                brand !== 'dkids' && (
                                    <>
                                        <InlineNav
                                            items={scheduleLinks.slice(0, 2)}
                                            gap="32"
                                        />
                                        <InlineNav
                                            items={scheduleLinks.slice(2)}
                                            gap="32"
                                        />
                                    </>
                                )}
                            {hasOT && (
                                <CookieLink className="ot-sdk-show-settings">
                                    {t('footerV2.cookies')}
                                </CookieLink>
                            )}
                        </Flex>

                        <Disclaimer>
                            {brand === 'dkids'
                                ? t('dkids.copyright')
                                : t('footerV2.disclaimer')}
                        </Disclaimer>
                    </DisclaimerContainer>
                </Flex>
            </Wrapper>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff !important;
    background-color: ${({brand}) => (brand === 'dkids' ? '#0D1744' : 'black')};
    z-index: 1;
    margin: 0;
    ${({paddingBottom}) => `padding-bottom: ${paddingBottom}px`}

    ${({showDetailsBTW}) =>
        showDetailsBTW &&
        css`
            padding-bottom: 220px;
        `}

    ${({noBottomNav}) =>
        noBottomNav &&
        css`
            margin-bottom: -64px;
        `}
`;

const Wrapper = styled(PageBodyWrapper)`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    width: 100%;
    padding: 50px 40px;
    box-sizing: border-box;
    max-width: 1440px;

    ${tabletMediaQuery} {
        padding-bottom: 23.5px 40px;
    }

    ${mobileMediaQuery} {
        padding: 50px 25px;
    }
`;

const LogoRow = styled(Flex)`
    display: flex;
    justify-content: flex-start;
    margin: 0 0 12px 0;
    flex: 1;

    & img {
        margin-bottom: 1rem;
    }

    & img:not(:last-child) {
        margin-right: 1rem;
    }
    & a:not(:last-child) {
        margin-right: 1rem;
    }

    ${mobileMediaQuery} {
        align-items: flex-start;
        flex-direction: column;
        width: 100%;
        margin: 0px;
    }
`;

const FooterDescription = styled.p`
    font-size: 16px;
    font-weight: normal;
    line-height: 22px;
    margin: 0 0 25px 0;
    white-space: pre-line;
`;

const DownloadText = styled.p`
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
    margin: 0;
    min-width: 301px;
`;

const Disclaimer = styled.p`
    font-size: 12px;
    line-height: 18px;
    max-width: 648px;
    margin: 8px auto 0 0;

    ${notWebMediaQuery} {
        margin-bottom: 40px;
    }
`;

const ImageLink = styled.a`
    margin-right: 24px;
`;

const CookieLink = styled.a`
    cursor: pointer;
    text-decoration: underline;
`;

const DisclaimerContainer = styled.div`
    flex-direction: column;

    ${({horizontal}) =>
        horizontal &&
        css`
            margin-left: 90px;
            max-width: 534px;
        `}
`;

export default Footer;
